/**
 * The browser half of the interactive sign-in — LV §133, §135, Arch §16.
 *
 * The backend is a backend-for-frontend: it performs the code exchange, keeps the
 * refresh token in an `HttpOnly` cookie this file cannot read, and hands back only a
 * short-lived access token. So there is deliberately no OIDC library here and no PKCE
 * arithmetic — the parts that would need a secret all live on the server.
 *
 * What this module does own:
 *
 * 1. **Picking the token out of the URL fragment** the backend redirected to, and
 *    erasing it from the address bar immediately. A fragment is never sent to a server
 *    and never lands in an access log, which is why the backend uses one — but it does
 *    stay in the browser's history until it is replaced.
 * 2. **Silent renewal.** LV §135 wants a short access-token lifetime, which is only
 *    usable if the token can be renewed without an interactive sign-in.
 *
 * The token itself is handed to `session.setToken`, which keeps it in a module-scoped
 * variable — never `localStorage` (LV §8, §128).
 */

import { clearSession, setToken } from "@/auth/session";

const API = "/api/v1/auth";

/**
 * Both calls in this file bypass `services/http.ts` — they run before there is a
 * session for it to attach — so they also miss its 30 s abort, and a bare `fetch` has
 * no timeout at all. On a plant tablet associated to an access point with no route the
 * connection stalls rather than being refused, and the sign-in screen sat on
 * "Anmeldung wird geprüft…" indefinitely: no heading, no error, no way to the token
 * form, and a reload that re-ran the same hanging request.
 *
 * Ten seconds rather than thirty: nothing here is a business request, both calls are
 * on the path to the first pixel a user can act on, and both have a defined answer for
 * failure (`false` / `null`).
 */
const AUTH_TIMEOUT_MS = 10_000;

export interface AuthConfig {
  auth_mode: string;
  interactive_login_available: boolean;
  dev_token_available: boolean;
  login_url: string;
  /**
   * `LOCAL` | `DEV` | `TEST` | `STAGING` | `PROD` — the server's own code, shown verbatim.
   *
   * **Optional, and that is not pedantry.** The api and the web container are two
   * separate App Service site containers rolled out by two separate `sitecontainers
   * update` calls, so "frontend newer than backend" is a real state and not a
   * hypothetical — an api that predates this field simply omits it. Typed as present, the
   * comparison `environment === "PROD"` would be `undefined === "PROD"`, i.e. false, and
   * the productive system would announce itself as "Testumgebung undefined". Both use
   * sites therefore test for the value rather than against `"PROD"`.
   */
  environment?: string;
  release_version?: string;
}

/**
 * What this server actually offers.
 *
 * Asked rather than assumed: the sign-in screen used to show "Mit Microsoft-Konto
 * anmelden" unconditionally, and pressing it reached a route that did not exist.
 */
export async function fetchAuthConfig(): Promise<AuthConfig | null> {
  try {
    const response = await fetch(`${API}/config`, {
      credentials: "same-origin",
      signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    return (await response.json()) as AuthConfig;
  } catch {
    // Offline, the API is down, or the request timed out — an `AbortError` lands here
    // like any other. `null` is the caller's "could not ask", and it decides per option
    // what to do with that: the interactive button stays (the server answers for itself
    // when the redirect is followed), the dev-token form does not.
    return null;
  }
}

/** Send the browser to the provider, asking to come back where we are now. */
export function startLogin(): void {
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.href = `${API}/login?next=${encodeURIComponent(next || "/")}`;
}

/**
 * Consume `#access_token=…` if the backend put one there.
 *
 * Returns true when a session was established, so the caller knows not to render the
 * sign-in screen. The fragment is stripped with `replaceState` rather than by assigning
 * `location.hash`, which would add a second history entry containing the token.
 */
export function consumeTokenFromFragment(): boolean {
  const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
  if (!raw) return false;

  const parameters = new URLSearchParams(raw);
  const token = parameters.get("access_token");
  const failure = parameters.get("error");
  if (!token && !failure) return false;

  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${window.location.search}`,
  );

  if (failure) {
    lastError = failure;
    return false;
  }
  if (!token) return false;

  setToken(token, Number(parameters.get("expires_in") ?? 0) || undefined);
  return true;
}

let lastError: string | null = null;

/** The provider's error code from the last callback, if it refused. */
export function takeLoginError(): string | null {
  const error = lastError;
  lastError = null;
  return error;
}

let renewal: Promise<boolean> | null = null;

/**
 * Renew the access token from the refresh cookie (LV §135).
 *
 * `credentials: "same-origin"` is what sends the cookie at all. Returns false when
 * there is no session to renew, which is the normal state before the first sign-in and
 * must not be treated as an error.
 *
 * **One renewal at a time** (#411). The dashboard fires several requests at once, and
 * when the token has run out every one of them is refused. Each used to start its own
 * renewal on the same refresh cookie, and the first to fail cleared the session another
 * had renewed a moment earlier. Callers arriving while a renewal is running now share it.
 */
export function refreshAccessToken(): Promise<boolean> {
  renewal ??= requestRenewal().finally(() => {
    renewal = null;
  });
  return renewal;
}

/**
 * What the HTTP client calls on a 401: renew, and end the session only if that fails.
 *
 * Resolves whether a fresh token is now held, so the client can repeat the request that
 * was refused (#411) instead of leaving the page broken until a reload.
 */
export async function renewSession(): Promise<boolean> {
  const renewed = await refreshAccessToken();
  if (!renewed) clearSession();
  return renewed;
}

async function requestRenewal(): Promise<boolean> {
  try {
    const response = await fetch(`${API}/refresh`, {
      method: "POST",
      credentials: "same-origin",
      signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
    });
    if (!response.ok) return false;
    const body = (await response.json()) as {
      access_token: string;
      expires_in: number;
    };
    if (!body.access_token) return false;
    setToken(body.access_token, body.expires_in);
    return true;
  } catch {
    return false;
  }
}

/**
 * End the session here, then at the provider if it offers a sign-out URL.
 *
 * The local session is cleared first and unconditionally: a provider that is
 * unreachable must not leave the user signed in to this application.
 */
export async function logout(): Promise<void> {
  let providerUrl: string | null = null;
  try {
    const response = await fetch(`${API}/logout`, {
      method: "POST",
      credentials: "same-origin",
    });
    if (response.ok) {
      providerUrl = ((await response.json()) as { provider_logout_url: string | null })
        .provider_logout_url;
    }
  } catch {
    // Ignored on purpose — see above.
  }
  clearSession();
  if (providerUrl) window.location.href = providerUrl;
}
